/* eslint-disable custom/no-restricted-error-message */
import getConfig from 'next/config';

import {
  BaseTransportOptions,
  Options,
  ErrorEvent,
  EventHint,
} from '@sentry/types';

const { publicRuntimeConfig } = getConfig() || {};

const THIRD_PARTY_ERROR_CHECKERS: Array<(error: Error) => boolean> = [
  // observed in recaptcha
  e =>
    /^undefined is not an object \(evaluating '\w\.didTimeout'\)$/.test(
      e.message,
    ),
  // This unhandled error is from third party flipbase video player.min.js. It gets logged in browser console only while user does fast forwarding of videos.
  // It does not have any impact and fast forwarding works as expected. It also happens in flipbase official documentation of demo video lol!!
  e => e.message === 'procent is not defined',
  // This unhandled error is from third party flipbase video player.min.js. It gets logged in browser console only while user does fast forwarding of videos.
  // It does not have any impact and fast forwarding works as expected. It also happens in flipbase official documentation of demo video lol!!
  e => e.message === "Can't find variable: procent",
  // This is apparently happens in WebView to due some issues with Chromium. More here: https://issues.chromium.org/issues/40761613
  e => e.message === 'Java object is gone',
  // observed in onetrust
  e => e.message === "undefined is not an object (evaluating 't.Domain')",
  // observed in onetrust
  e =>
    e.message.endsWith(
      '(type=unhandledrejection) captured as promise rejection',
    ),
  // observed in recaptcha and google analytics
  e => e.message === 'Non-Error promise rejection captured with value: Timeout',
  // This error happens when downloading file h1-vendors-main-popover.js which is part of the source code for the Honey browser extension
  e => e.message === 'NotFoundError',
  // observed in onetrust
  e =>
    e.message ===
    "null is not an object (evaluating 'this.selector.appendChild')",
  // observed in recaptcha
  e => e.message === "Cannot read properties of undefined (reading 'get')",
  // I couldn't determine which third-party script is causing this error, but I assume it's not from our code since
  // there's absolutely no stack trace or any useful information to help identify the root cause.
  e => e.message === 'Unexpected end of script',
  // I couldn't determine which third-party script is causing this error, but I assume it's not from our code since
  // there's absolutely no stack trace or any useful information to help identify the root cause.
  e => e.message === 'Maximum call stack size exceeded.',
  // This can be from any third party script. All Kamernet api calls are done via ApiClient which
  // throws a meaningful error. So any error with this message is not ours
  e => e.message === 'Unexpected end of JSON input',
];

export const filterThirdPartyErrors = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  const error = hint.originalException;

  if (error instanceof Error) {
    for (const checker of THIRD_PARTY_ERROR_CHECKERS) {
      if (checker(error)) {
        return null;
      }
    }
  }

  return event;
};

export const SENTRY_BASE_CONFIG: Options<BaseTransportOptions> = {
  debug: false,
  release: process.env.BUILD_ID,
  dsn: publicRuntimeConfig?.SENTRY_DSN || '',
  sampleRate: publicRuntimeConfig?.SENTRY_SAMPLE_RATE || 0,
  tracesSampleRate: publicRuntimeConfig?.SENTRY_TRACES_SAMPLE_RATE || 0,
  environment: publicRuntimeConfig?.DEPLOYMENT_ID || '',
  normalizeDepth: publicRuntimeConfig?.SENTRY_NORMALIZE_DEPTH || 0,
  beforeSend: filterThirdPartyErrors,
};
